import { ReactNode } from 'react'
import { Check } from '@phosphor-icons/react'

export interface WizardStep {
  id: string
  title: string
  description?: string
  icon?: ReactNode
}

interface WizardProps {
  steps: WizardStep[]
  currentStep: number
  children: ReactNode
}

export function Wizard({ steps, currentStep, children }: WizardProps) {
  return (
    <div className="space-y-8">
      {/* Progress Steps */}
      <div className="relative">
        {/* Progress Bar Background */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200" style={{ zIndex: 0 }} />

        {/* Progress Bar Active */}
        <div
          className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500"
          style={{
            width: `${(currentStep / (steps.length - 1)) * 100}%`,
            zIndex: 1
          }}
        />

        {/* Steps */}
        <div className="relative flex justify-between" style={{ zIndex: 2 }}>
          {steps.map((step, index) => {
            const isCompleted = index < currentStep
            const isCurrent = index === currentStep
            const isUpcoming = index > currentStep

            return (
              <div key={step.id} className="flex flex-col items-center">
                {/* Step Circle */}
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                    transition-all duration-300 relative
                    ${isCompleted
                      ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg scale-100'
                      : isCurrent
                      ? 'bg-white border-4 border-emerald-500 text-emerald-600 shadow-lg scale-110'
                      : 'bg-white border-2 border-gray-300 text-gray-400'
                    }
                  `}
                >
                  {isCompleted ? (
                    <Check size={20} weight="bold" className="animate-scale-in" />
                  ) : (
                    <span>{index + 1}</span>
                  )}

                  {/* Pulse animation for current step */}
                  {isCurrent && (
                    <span className="absolute inset-0 rounded-full border-4 border-emerald-400 animate-ping opacity-75" />
                  )}
                </div>

                {/* Step Label */}
                <div className="mt-3 text-center max-w-[120px]">
                  <p
                    className={`text-sm font-semibold transition-colors ${
                      isCurrent
                        ? 'text-emerald-600'
                        : isCompleted
                        ? 'text-gray-700'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.title}
                  </p>
                  {step.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="animate-fade-in">{children}</div>

      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }

        .animate-fade-in {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </div>
  )
}

interface WizardActionsProps {
  onBack?: () => void
  onNext?: () => void
  onSubmit?: () => void
  onSaveDraft?: () => void
  isFirstStep?: boolean
  isLastStep?: boolean
  isSubmitting?: boolean
  nextLabel?: string
  backLabel?: string
  submitLabel?: string
  saveDraftLabel?: string
  canProgress?: boolean
}

export function WizardActions({
  onBack,
  onNext,
  onSubmit,
  onSaveDraft,
  isFirstStep = false,
  isLastStep = false,
  isSubmitting = false,
  nextLabel = 'Próximo',
  backLabel = 'Voltar',
  submitLabel = 'Finalizar Cotação',
  saveDraftLabel = 'Salvar Rascunho',
  canProgress = true
}: WizardActionsProps) {
  return (
    <div className="flex items-center justify-between pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={onBack}
        disabled={isFirstStep || isSubmitting}
        className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
          isFirstStep || isSubmitting
            ? 'opacity-0 pointer-events-none'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        ← {backLabel}
      </button>

      {isLastStep ? (
        <div className="flex items-center gap-3">
          {onSaveDraft && (
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saveDraftLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting || !canProgress}
            className="px-8 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
          >
            {isSubmitting ? 'Enviando...' : submitLabel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onNext}
          disabled={isSubmitting || !canProgress}
          className="px-8 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
        >
          {nextLabel} →
        </button>
      )}
    </div>
  )
}
